import axios from 'axios'
import { installConnectionInterceptor } from './connection'

export const api = axios.create({
  baseURL: (import.meta as any).env?.VITE_API_BASE_URL || '/api/v1',
  headers: { 'Content-Type': 'application/json' },
})

api.interceptors.request.use(config => {
  const authKey = (window as any).PILOT_HUB_CONFIG?.authStorageKey || 'pilot-hub-auth'
  const authData = localStorage.getItem(authKey)
  if (authData) {
    try {
      const parsed = JSON.parse(authData)
      const token = parsed.accessToken || parsed.access_token || parsed.state?.accessToken || parsed.state?.access_token
      if (token) config.headers.Authorization = `Bearer ${token}`
    } catch {}
  }
  return config
})

api.interceptors.response.use(
  res => res,
  async err => {
    if (err.response?.status === 401 && err.config?.url !== '/auth/refresh') {
      try {
        const authKey = (window as any).PILOT_HUB_CONFIG?.authStorageKey || 'pilot-hub-auth'
        const authData = localStorage.getItem(authKey)
        if (!authData) throw new Error('No auth data')
        
        const parsed = JSON.parse(authData)
        const refresh = parsed.refreshToken || parsed.refresh_token || parsed.state?.refreshToken || parsed.state?.refresh_token
        
        if (!refresh) throw new Error('No refresh token')
        
        const res = await axios.post(`${api.defaults.baseURL}/auth/refresh`, { refresh_token: refresh })
        
        // Update localStorage with new tokens
        const updatedAuth = {
          ...parsed,
          accessToken: res.data.access_token,
          access_token: res.data.access_token,
          refreshToken: res.data.refresh_token || refresh,
          refresh_token: res.data.refresh_token || refresh,
          state: {
            ...parsed.state,
            accessToken: res.data.access_token,
            access_token: res.data.access_token,
            refreshToken: res.data.refresh_token || refresh,
            refresh_token: res.data.refresh_token || refresh,
          }
        }
        localStorage.setItem(authKey, JSON.stringify(updatedAuth))
        
        // Update axios default header
        api.defaults.headers.common['Authorization'] = `Bearer ${res.data.access_token}`
        err.config.headers.Authorization = `Bearer ${res.data.access_token}`
        
        return api.request(err.config)
      } catch {
        const authKey = (window as any).PILOT_HUB_CONFIG?.authStorageKey || 'pilot-hub-auth'
        localStorage.removeItem(authKey)
        if (window.location.pathname !== '/login') window.location.href = '/login'
      }
    }
    // FastAPI-Validierungsfehler (422) liefern eine Liste von Objekten. Die Seiten zeigen `detail` direkt an;
    // ein Objekt dort lässt React abstürzen. Daher hier in lesbaren Text umwandeln.
    const daten = err.response?.data
    if (daten && Array.isArray(daten.detail)) {
      daten.detail = 'Eingabe ungültig – ' + daten.detail.map((d: any) => {
        const feld = Array.isArray(d?.loc) ? d.loc.filter((x: unknown) => x !== 'body').join('.') : ''
        const text = d?.type === 'missing' ? 'fehlt' : d?.msg ?? String(d)
        return feld ? `${feld}: ${text}` : text
      }).join('; ')
    } else if (daten?.detail && typeof daten.detail === 'object') {
      daten.detail = JSON.stringify(daten.detail)
    }
    return Promise.reject(err)
  }
)

// Verbindungsstatus: muss NACH dem Refresh-Interceptor registriert werden (sieht nur das Endergebnis)
installConnectionInterceptor(api)

// Auth API
export const authApi = {
  login: (data: { benutzername: string; password: string }) => api.post('/auth/login', data),
  register: (data: any) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  updateProfile: (data: any) => api.put('/auth/me', data),
  changePassword: (data: any) => api.post('/auth/change-password', data),
  refresh: (refresh_token: string) => api.post('/auth/refresh', { refresh_token }),
}

// User API
export const userApi = {
  list: (params?: any) => api.get('/nutzer', { params }),
  get: (id: number) => api.get(`/nutzer/${id}`),
  create: (data: any) => api.post('/nutzer', data),
  update: (id: number, data: any) => api.put(`/nutzer/${id}`, data),
  delete: (id: number) => api.delete(`/nutzer/${id}`),
  getRoles: () => api.get('/rollen'),
  getFunktionen: () => api.get('/funktionen'),
  setRollen: (id: number, rollen: string[]) => api.post(`/nutzer/${id}/rollen`, { rollen }),
  setFunktionen: (id: number, funktionen: string[]) => api.post(`/nutzer/${id}/funktionen`, { funktionen }),
  getLoginAdmins: () => api.get('/nutzer/login-admins'),
  getLoginFuehrung: () => api.get('/nutzer/fuehrung/login'),
  getLoginPilots: () => api.get('/nutzer/login-pilots'),
  getLoginLeitstelle: () => api.get('/nutzer/login-leitstelle'),
}

// Drone API
export const droneApi = {
  list: (params?: any) => api.get('/drohnen', { params }),
  get: (id: number) => api.get(`/drohnen/${id}`),
  create: (data: any) => api.post('/drohnen', data),
  update: (id: number, data: any) => api.put(`/drohnen/${id}`, data),
  delete: (id: number) => api.delete(`/drohnen/${id}`),
  getSelection: () => api.get('/drohnen/auswahl'),
  getAkkus: (droneId: number) => api.get(`/drohnen/${droneId}/akkus`),
  getReferenzen: () => api.get('/drohnen/referenzen'),
  getDroneUtilization: () => api.get('/drohnen/utilization'),
  getDropdownList: () => api.get('/drohnen/auswahl'),
}

// Akku API
export const akkuApi = {
  list: (params?: any) => api.get('/akkus', { params }),
  get: (id: number) => api.get(`/akkus/${id}`),
  create: (data: any) => api.post('/akkus', data),
  update: (id: number, data: any) => api.put(`/akkus/${id}`, data),
  delete: (id: number) => api.delete(`/akkus/${id}`),
  getTypes: () => api.get('/akku-typen'),
}

// Vehicle API
export const vehicleApi = {
  list: (params?: any) => api.get('/fahrzeuge', { params }),
  get: (id: number) => api.get(`/fahrzeuge/${id}`),
  create: (data: any) => api.post('/fahrzeuge', data),
  update: (id: number, data: any) => api.put(`/fahrzeuge/${id}`, data),
  delete: (id: number) => api.delete(`/fahrzeuge/${id}`),
  getDropdownList: () => api.get('/fahrzeuge/dropdown'),
}

// License API
export const licenseApi = {
  list: (params?: any) => api.get('/lizenzen', { params }),
  get: (id: number) => api.get(`/lizenzen/${id}`),
  create: (data: any) => api.post('/lizenzen', data),
  update: (id: number, data: any) => api.put(`/lizenzen/${id}`, data),
  delete: (id: number) => api.delete(`/lizenzen/${id}`),
  getTypes: () => api.get('/lizenzen/typen'),
  getUserLicenses: (userId: number) => api.get(`/nutzer/${userId}/lizenzen`),
  createUserLicense: (userId: number, data: any) => api.post(`/nutzer/${userId}/lizenzen`, data),
  deleteUserLicense: (userId: number, licenseId: number) => api.delete(`/nutzer/${userId}/lizenzen/${licenseId}`),
}

// Mission API
export const missionApi = {
  list: (params?: any) => api.get('/einsaetze', { params }),
  get: (id: number) => api.get(`/einsaetze/${id}`),
  create: (data: any) => api.post('/einsaetze', data),
  update: (id: number, data: any) => api.put(`/einsaetze/${id}`, data),
  delete: (id: number) => api.delete(`/einsaetze/${id}`),
  getTeilnehmer: (einsatzId: number) => api.get(`/einsaetze/${einsatzId}/teilnehmer`),
  addTeilnehmer: (einsatzId: number, nutzerId: number) => api.post(`/einsaetze/${einsatzId}/teilnehmer`, { nutzer_id: nutzerId }),
  removeTeilnehmer: (einsatzId: number, nutzerId: number) => api.delete(`/einsaetze/${einsatzId}/teilnehmer/${nutzerId}`),
  getActive: (nutzerId: number) => api.get(`/einsaetze/active?nutzer_id=${nutzerId}`),
  getPilots: (einsatzId: number) => api.get(`/einsaetze/${einsatzId}/pilots`),
  getActiveFlights: (missionId: number) => api.get(`/einsaetze/${missionId}/flights/active`),
  getAllFlights: (missionId: number) => api.get(`/einsaetze/${missionId}/flights`),
  getForFlight: () => api.get('/einsaetze/flug-erfassen'),
  getMeinKraefteEinsatz: (nutzerId: number) => api.get('/einsaetze/mein-kraefte-einsatz', { params: { nutzer_id: nutzerId } }),
}

// Flight API
export const flightApi = {
  list: (params?: any) => api.get('/fluege', { params }),
  /** laufende Flüge: ohne Parameter die eigenen (als Pilot), mit einsatz_id alle des Einsatzes (Co-Pilot) */
  aktive: (params?: { einsatz_id?: number }) => api.get('/fluege/aktive', { params }),
  get: (id: number) => api.get(`/fluege/${id}`),
  create: (data: any) => api.post('/fluege', data),
  update: (id: number, data: any) => api.put(`/fluege/${id}`, data),
  delete: (id: number) => api.delete(`/fluege/${id}`),
  end: (id: number, data?: any) => api.put(`/fluege/${id}/ende`, data),
  getDrones: () => api.get('/drohnen/auswahl'),
  getAkkus: (droneId: number) => api.get(`/drohnen/${droneId}/akkus`),
  getEinsaetze: () => api.get('/einsaetze/flug-erfassen')
}

// Dashboard API
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
  getStatsV2: () => api.get('/dashboard/stats-v2'),
}

// Dashboard API (Führungskräfte)
export const dashboardFuehrungApi = {
  getEinsatzDashboard: (einsatzId: number) => api.get(`/dashboard/einsatz/${einsatzId}`),
}

// Settings API
export const settingsApi = {
  get: () => api.get('/settings'),
  update: (data: any) => api.put('/settings', data),
}

// Function API
export const functionApi = {
  list: () => api.get('/funktionen'),
  create: (data: any) => api.post('/funktionen', data),
  update: (id: number, data: any) => api.put(`/funktionen/${id}`, data),
  delete: (id: number) => api.delete(`/funktionen/${id}`),
}

// Storage API
export const storageApi = {
  upload: (data: FormData) => api.post('/storage/upload', data),
  download: (key: string) => api.get(`/storage/download/${key}`, { responseType: 'blob' }),
  delete: (key: string) => api.delete(`/storage/${key}`),
}

// Export API
export const exportApi = {
  download: (resource: string, params?: any) => api.get(`/export/${resource}`, { params, responseType: 'blob' }),
}

// Akku Type API
export const akkuTypeApi = {
  list: () => api.get('/akku-typen'),
}

// Checklisten API
export const checklistenApi = {
  // Vorlagen
  listVorlagen: (params?: any) => api.get('/checklisten/vorlagen', { params }),
  getVorlage: (id: number) => api.get(`/checklisten/vorlagen/${id}`),
  /** Punkte der Vorflug-Checkliste eines Einsatzes (Pilot-API), vor dem Flugstart auszufüllen */
  getVorflugVorlage: (einsatzId: number) => api.get('/checklisten/vorflug-vorlage', { params: { einsatz_id: einsatzId } }),
  createVorlage: (data: any) => api.post('/checklisten/vorlagen', data),
  updateVorlage: (id: number, data: any) => api.put(`/checklisten/vorlagen/${id}`, data),
  deleteVorlage: (id: number) => api.delete(`/checklisten/vorlagen/${id}`),
  
  // Instanzen
  getEinsatzChecklisten: (einsatzId: number) => api.get(`/einsaetze/${einsatzId}/checklisten`),
  getInstanz: (instanzId: number) => api.get(`/checklisten/instanzen/${instanzId}`),
  updateInstanz: (instanzId: number, data: any) => api.put(`/checklisten/instanzen/${instanzId}`, data),
  updateInstanzItem: (instanzId: number, itemId: number, data: any) => api.put(`/checklisten/instanzen/${instanzId}/items/${itemId}`, data),
  
  // Status
  getEinsatzChecklistenStatus: (einsatzId: number) => api.get(`/einsaetze/${einsatzId}/checklisten-status`),
  getFluegeChecklistenStatus: (einsatzId: number) => api.get(`/einsaetze/${einsatzId}/fluege-checklisten-status`),
}

export default api
